import { useEffect, useState } from "react";
import api from "../services/api";
import { getUserInfoFromToken, isAgent } from "../services/auth";
import { useNavigate } from "react-router-dom";
import { Upload, UserPlus, Pencil, Trash2, ClipboardPaste } from "lucide-react";
import PageHeader from "../components/common/PageHeader";
import Button from "../components/common/Button";
import SearchBar from "../components/common/SearchBar";
import { toggleCustomerStatus } from "../services/customerServices";
import BulkCustomerUpload from "../components/BulkCustomerUpload";
import AddSingleCustomerModal from "../components/AddSingleCustomerModal";
import EditCustomerModal from "../components/EditCustomerModal";
import QuickImportModal from "../components/QuickImportModal";
import Pagination from "../components/Pagination";
import LoadingSpinner from "../components/LoadingSpinner";

const CustomerDetails = () => {
  const agentRole = isAgent();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [showQuickImport, setShowQuickImport] = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showDeleteSelectedConfirm, setShowDeleteSelectedConfirm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  const customersPerPage = 10;

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/customers/by-user`);
      setCustomers(res.data.customers);
    } catch (error) {
      console.error("Error fetching customers:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const { userId } = getUserInfoFromToken();
    if (!userId) return alert("User ID not found in token");
    fetchCustomers();
  }, []);

  const handleToggle = async (id) => {
    try {
      const res = await toggleCustomerStatus(id);
      setCustomers((prev) =>
        prev.map((c) =>
          c.id === id ? { ...c, status: res.customer.status } : c
        )
      );
    } catch (err) {
      console.error("Failed to toggle status", err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/customers/${id}`);
      setCustomers((prev) => prev.filter((c) => c.id !== id));
      setSelectedIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
      setDeleteConfirmId(null);
    } catch (err) {
      console.error("Failed to delete customer", err);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    const allIds = filteredCustomers.map((c) => c.id);
    const allSelected = allIds.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        allIds.forEach((id) => next.delete(id));
      } else {
        allIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleDeleteSelected = async () => {
    try {
      await api.delete("/customers/bulk", {
        data: { customerIds: Array.from(selectedIds) },
      });
      setCustomers((prev) => prev.filter((c) => !selectedIds.has(c.id)));
      setSelectedIds(new Set());
      setShowDeleteSelectedConfirm(false);
    } catch (err) {
      console.error("Failed to delete selected customers", err);
    }
  };

  // Filter customers based on search query
  const filteredCustomers = customers.filter((customer) => {
    const searchLower = searchQuery.toLowerCase().trim();
    if (!searchLower) return true;

    return (
      customer.name?.toLowerCase().includes(searchLower) ||
      customer.phoneNumber?.toLowerCase().includes(searchLower) ||
      customer.email?.toLowerCase().includes(searchLower)
    );
  });

  // Pagination calculations
  const indexOfLastCustomer = currentPage * customersPerPage;
  const indexOfFirstCustomer = indexOfLastCustomer - customersPerPage;
  const currentCustomers = filteredCustomers.slice(
    indexOfFirstCustomer,
    indexOfLastCustomer
  );
  const totalPages = Math.ceil(filteredCustomers.length / customersPerPage);

  const allCurrentSelected =
    filteredCustomers.length > 0 &&
    filteredCustomers.every((c) => selectedIds.has(c.id));
  const someCurrentSelected = filteredCustomers.some((c) => selectedIds.has(c.id));

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  return (
    <div className="p-4 md:p-8">
      <PageHeader
        title="Customer Details"
        action={
          !agentRole && (
            <div className="flex gap-2 flex-wrap">
              {selectedIds.size > 0 && (
                <Button
                  variant="danger"
                  onClick={() => setShowDeleteSelectedConfirm(true)}
                  icon={<Trash2 className="w-4 h-4" />}
                >
                  Delete Selected ({selectedIds.size})
                </Button>
              )}
              <Button
                variant="secondary"
                onClick={() => setShowAddCustomer(true)}
                icon={<UserPlus className="w-4 h-4" />}
              >
                Add Customer
              </Button>
              <Button
                variant="secondary"
                onClick={() => setShowQuickImport(true)}
                icon={<ClipboardPaste className="w-4 h-4" />}
              >
                Quick Import
              </Button>
              <Button
                variant="primary"
                onClick={() => setShowBulkImport(true)}
                icon={<Upload className="w-4 h-4" />}
              >
                Bulk Import
              </Button>
            </div>
          )
        }
      />

      {/* Search Bar */}
      <div className="mb-4">
        <SearchBar
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by name, phone number, or email..."
          className="max-w-md"
        />
      </div>

      {/* Results Count */}
      {searchQuery && (
        <div className="mb-4 text-sm text-gray-600">
          Found {filteredCustomers.length} customer
          {filteredCustomers.length !== 1 ? "s" : ""}
        </div>
      )}

      {/* Table Section */}
      <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
        {loading ? (
          <LoadingSpinner fullScreen size="lg" text="Loading customers..." />
        ) : customers.length === 0 ? (
          <p className="text-gray-600">No customers found.</p>
        ) : filteredCustomers.length === 0 ? (
          <p className="text-gray-600">
            No customers match your search criteria.
          </p>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="block md:hidden space-y-4">
              {currentCustomers.map((c, index) => (
                <div
                  key={c.id}
                  className={`rounded-lg p-4 border transition ${
                    selectedIds.has(c.id)
                      ? "bg-red-50 border-red-200"
                      : "bg-gray-50 border-gray-200"
                  }`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-start gap-3">
                      {!agentRole && (
                        <input
                          type="checkbox"
                          checked={selectedIds.has(c.id)}
                          onChange={() => handleSelectOne(c.id)}
                          className="mt-1 w-4 h-4 rounded border-gray-300 text-red-600 cursor-pointer"
                        />
                      )}
                      <div>
                        <h3 className="font-semibold text-gray-800">
                          #{indexOfFirstCustomer + index + 1} {c.name}
                        </h3>
                        <p className="text-sm text-gray-600">{c.phoneNumber}</p>
                        {c.email && (
                          <p className="text-sm text-gray-500">{c.email}</p>
                        )}
                      </div>
                    </div>
                    {!agentRole && (
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={c.status}
                          onChange={() => handleToggle(c.id)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-blue-600 transition duration-300"></div>
                        <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform duration-300 transform peer-checked:translate-x-full"></div>
                      </label>
                    )}
                  </div>
                  <div className="text-sm text-gray-500 space-y-1">
                    <p>
                      <span className="font-medium">Created:</span>{" "}
                      {new Date(c.createdAt).toLocaleDateString()}
                    </p>
                    <p>
                      <span className="font-medium">Messages Sent:</span>{" "}
                      {c.totalMessagesSent}
                    </p>
                  </div>
                  {!agentRole && (
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200">
                      <button
                        onClick={() => setEditCustomer(c)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                        Edit
                      </button>
                      {deleteConfirmId === c.id ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleDelete(c.id)}
                            className="px-3 py-1.5 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 transition"
                          >
                            Confirm Delete
                          </button>
                          <button
                            onClick={() => setDeleteConfirmId(null)}
                            className="px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirmId(c.id)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Delete
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-y-auto" style={{ maxHeight: "calc(100vh - 260px)" }}>
              <table className="min-w-full table-auto text-sm">
                <thead>
                  <tr>
                    {!agentRole && (
                      <th className="sticky top-0 z-10 px-3 py-2.5 bg-gray-50 border-b border-gray-200">
                        <input
                          type="checkbox"
                          checked={allCurrentSelected}
                          ref={(el) => {
                            if (el) el.indeterminate = someCurrentSelected && !allCurrentSelected;
                          }}
                          onChange={handleSelectAll}
                          className="w-4 h-4 rounded border-gray-300 text-red-600 cursor-pointer"
                          title="Select all on this page"
                        />
                      </th>
                    )}
                    <th className="sticky top-0 z-10 px-3 py-2.5 bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200 w-10">#</th>
                    <th className="sticky top-0 z-10 px-3 py-2.5 bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200">Name</th>
                    <th className="sticky top-0 z-10 px-3 py-2.5 bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200">Phone</th>
                    <th className="sticky top-0 z-10 px-3 py-2.5 bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200">Email</th>
                    <th className="sticky top-0 z-10 px-3 py-2.5 bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200">Created</th>
                    <th className="sticky top-0 z-10 px-3 py-2.5 bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200">Msgs Sent</th>
                    {!agentRole && (
                      <th className="sticky top-0 z-10 px-3 py-2.5 bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200">Status</th>
                    )}
                    {!agentRole && (
                      <th className="sticky top-0 z-10 px-3 py-2.5 bg-gray-50 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {currentCustomers.map((c, index) => (
                    <tr
                      key={c.id}
                      className={`transition-colors ${
                        selectedIds.has(c.id) ? "bg-red-50" : "hover:bg-gray-50"
                      }`}
                    >
                      {!agentRole && (
                        <td className="px-3 py-3">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(c.id)}
                            onChange={() => handleSelectOne(c.id)}
                            className="w-4 h-4 rounded border-gray-300 text-red-600 cursor-pointer"
                          />
                        </td>
                      )}
                      <td className="px-3 py-3 text-gray-400 text-xs">{indexOfFirstCustomer + index + 1}</td>
                      <td className="px-3 py-3 font-medium text-gray-800">{c.name}</td>
                      <td className="px-3 py-3 text-gray-600 font-mono text-xs">{c.phoneNumber}</td>
                      <td className="px-3 py-3 text-gray-500">{c.email || <span className="text-gray-300">—</span>}</td>
                      <td className="px-3 py-3 text-gray-500 whitespace-nowrap">
                        {new Date(c.createdAt).toLocaleDateString("en-US", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </td>
                      <td className="px-3 py-3 text-center text-gray-600">{c.totalMessagesSent}</td>
                      {!agentRole && (
                        <td className="px-3 py-3">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={c.status}
                              onChange={() => handleToggle(c.id)}
                              className="sr-only peer"
                            />
                            <div className="w-9 h-5 bg-gray-300 rounded-full peer peer-checked:bg-blue-500 transition duration-300"></div>
                            <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full transition-transform duration-300 transform peer-checked:translate-x-4"></div>
                          </label>
                        </td>
                      )}
                      {!agentRole && (
                        <td className="px-3 py-3">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => setEditCustomer(c)}
                              className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg transition"
                              title="Edit"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            {deleteConfirmId === c.id ? (
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => handleDelete(c.id)}
                                  className="px-2 py-1 text-xs text-white bg-red-600 rounded hover:bg-red-700 transition"
                                >
                                  Confirm
                                </button>
                                <button
                                  onClick={() => setDeleteConfirmId(null)}
                                  className="px-2 py-1 text-xs text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setDeleteConfirmId(c.id)}
                                className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg transition"
                                title="Delete"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {filteredCustomers.length > customersPerPage && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        )}
      </div>

      {/* Edit Customer Modal */}
      {editCustomer && (
        <EditCustomerModal
          customer={editCustomer}
          onClose={() => setEditCustomer(null)}
          onSuccess={fetchCustomers}
        />
      )}

      {/* Delete Selected Confirmation Modal */}
      {showDeleteSelectedConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Delete {selectedIds.size} Customer{selectedIds.size !== 1 ? "s" : ""}?
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              This will permanently delete the selected{" "}
              {selectedIds.size === 1 ? "customer" : `${selectedIds.size} customers`}{" "}
              and remove them from all groups. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteSelectedConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteSelected}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Single Customer Modal */}
      {showAddCustomer && (
        <AddSingleCustomerModal
          onClose={() => setShowAddCustomer(false)}
          onSuccess={fetchCustomers}
        />
      )}

      {/* Quick Import Modal */}
      {showQuickImport && (
        <QuickImportModal
          onClose={() => setShowQuickImport(false)}
          onSuccess={() => { setShowQuickImport(false); fetchCustomers(); }}
        />
      )}

      {/* Bulk Import Modal */}
      {showBulkImport && (
        <BulkCustomerUpload
          onUploadComplete={() => {
            setShowBulkImport(false);
            fetchCustomers();
          }}
          onClose={() => setShowBulkImport(false)}
        />
      )}
    </div>
  );
};

export default CustomerDetails;
